import {
    faCirclePlay,
    faClipboard,
    faEdit,
    faSearch,
    faStar,
    faTriangleExclamation,
    faUserPen
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DefaultTFuncReturn } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Box,
    Flex,
    Image,
    Modal,
    Table
} from '../../../../../components';
import { P } from '../../../../../components/Typography/Typography';
import { Button, Checkbox, TextField } from '../../../../../components/_form';
import {
    ChosenItemsHeader,
    DetailedHeaderWrapper,
    StyledP,
    Wrapper
} from './TaskTable.styled';

import { useQueryClient } from '@tanstack/react-query';
import { tasksKeys } from 'api/queryKeys';
import { getExecutionProgressTrackings } from 'api/executionProgressTrackings';
import { createTask, deleteTask, editTask } from 'api/tasks';
import { ExecutionProgressTracking } from 'components/ExecutionProgressTracking/ExecutionProgressTracking';
import { useUsersQuery } from 'hooks/queries/useUsersQuery';
import { ColumnsOptions } from 'components/Table/components/ColumnsOptions';
import { TaskStar } from 'components/TaskStar/TaskStar';
import { dateFormat } from 'constants/dateFormats/dateFormat';
import { useTaskSidebarContext } from 'contexts/TaskSidebarContext';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { useTasksQuery } from 'hooks/queries/useTasksQuery';
import { differenceInDays, format, isAfter } from 'date-fns';
import { useAccount } from 'hooks/useAccount';
import { debounce } from 'lodash';
import ReactDOMServer from 'react-dom/server';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { PriorityLevelBadge } from 'views/AdministrationView/views/TasksView/components/Priorities/components/PriorityLevelBadge/PriorityLevelBadge';
import { displayTranslatedField } from 'utilities/localizedString';
import { ITaskForm } from 'types/forms/task';
import { ExecutionProgressStatusFormValue } from 'types/forms/executionProgressStatuses';
import { IExecutionProgressTracking } from 'types/executionProgressTrackings';
import { ITask } from 'types/task';
import { IUser } from 'types/user';
import { durationIso8601ToHour } from 'utilities/durationIso8601ToHour';
import { durationIso8601ToMinute } from 'utilities/durationIso8601ToMinute';
import { syncExecutionProgressStatuses } from 'utilities/syncExecutionProgressStatuses';
import { theme } from '../../../../../styles';
import { TaskFormModal } from '../../TaskFormModal/TaskFormModal';
import { StarModal } from '../../TaskFormModal/components/StarModal';
import { CancelTaskModal } from './components/CancelTaskModal';
import { RemindLatecomersModal } from './components/RemindLatecomersModal';
import { SendNotificationsModal } from './components/SendNotificationsModal';
import { TopBar } from './components/TopBar';

interface Props {
  customTitle?: string;
  score_ids?: number[];
  person_in_charge_ids?: string[];
  team_ids?: string[];
  showTopBar?: boolean;
  showAdditionalHeader?: boolean;
  task_type_kinds?: string[];
  without_active?: boolean;
  without_archived?: boolean;
  without_cancelled?: boolean;
  loadContextFilters?: boolean;
  showDetailedHeader?: boolean;
  nameColMinWidth?: string;
  tableContainerHeight?: string;
  defaultHiddenItems?: string[];
  areItemsSelectable?: boolean;
}

export const TaskTable: React.FC<Props> = ({
  customTitle,
  score_ids,
  person_in_charge_ids,
  team_ids,
  task_type_kinds,
  without_active,
  without_archived,
  without_cancelled,
  loadContextFilters = true,
  showTopBar = true,
  showAdditionalHeader = true,
  showDetailedHeader,
  nameColMinWidth,
  tableContainerHeight,
  defaultHiddenItems = [],
  areItemsSelectable = true
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [hiddenItems, setHiddenItems] = useState<string[]>(defaultHiddenItems);
  const [IsFormModalOpen, setIsFormModalOpen] = useState(false);
  const [IsCancelTaskModalOpen, setIsCancelTaskModalOpen] = useState(false);
  const [IsArchiveTaskModalOpen, setIsArchiveTaskModalOpen] = useState(false);
  const [IsRemindLatecomersModalOpen, setIsRemindLatecomersModalOpen] =
    useState(false);
  const [IsSendNotificationsModalOpen, setIsSendNotificationsModalOpen] =
    useState(false);
  const [isStarModalOpen, setIsStarModalOpen] = useState(false);
  const [selectedItemData, setSelectedItemData] = useState<ITask>();
  const [checkedItemsIds, setCheckedItemsIds] = useState<number[]>([]);
  const [executionProgressTrackings, setExecutionProgressTrackings] = useState<
    IExecutionProgressTracking[]
  >([]);
  const { data: usersList = [] } = useUsersQuery();
  const queryClient = useQueryClient();
  const { data: mainCompany } = useMainCompanyQuery();
  const companyId = mainCompany?.id ?? '';

  const { filters, setFilters } = useTaskSidebarContext();

  const { loggedUserData } = useAccount();

  const filtersForQuery = useMemo(() => {
    const {
      typesOfTasks,
      taskPriorities,
      teamsAndGroups,
      authors,
      responsiblePersons,
      idCodeName,
      created,
      startDate,
      timeLimitForCompletion,
      implementation,
      hidden,
      starred
    } = filters;

    return {
      person_in_charge_ids: person_in_charge_ids ?? [],
      score_ids: score_ids ?? [],
      team_ids: team_ids ?? [],
      task_type_kinds: task_type_kinds ?? [],
      without_active: !!without_active,
      without_archived: !!without_archived,
      without_cancelled: !!without_cancelled,
      page,
      per_page: perPage,
      ...(loadContextFilters && {
        task_type_ids: typesOfTasks.value,
        priority_level_ids: taskPriorities.value,
        team_ids: teamsAndGroups.value,
        author_ids: authors.value,
        person_in_charge_ids: responsiblePersons.value,
        text: idCodeName.value,
        created_at_or_later: created.value.from,
        created_no_later_than: created.value.to,
        started_at_or_later: startDate.value.from,
        started_no_later_than: startDate.value.to,
        planned_completion_for_on_or_after: timeLimitForCompletion.value.from,
        planned_completion_for_no_later_than: timeLimitForCompletion.value.to,
        completed_at_or_later: implementation.value.from,
        completed_no_later_than: implementation.value.to,
        without_active: hidden.value.includes(1),
        without_archived: hidden.value.includes(2),
        without_cancelled: hidden.value.includes(3),
        starred_by_user_ids: starred.value.includes(1) ? [loggedUserData.id] : [],
        starred_by_teamed_up_ids: starred.value.includes(2)
          ? [loggedUserData.id]
          : [],
        starred_by_area_ids: starred.value.includes(3)
          ? loggedUserData.ownedAreas.map(({ id }) => id)
          : []
      })
    };
  }, [
    filters,
    loggedUserData.id,
    JSON.stringify(loggedUserData.ownedAreas),
    score_ids,
    person_in_charge_ids,
    team_ids,
    task_type_kinds,
    without_active,
    without_archived,
    without_cancelled,
    page,
    perPage,
    loadContextFilters
  ]);

  const { data: tasksResponse, refetch: refetchTasks } = useTasksQuery(
    filtersForQuery
  );
  const tasks = tasksResponse?.data ?? [];
  const paginationTotal = Number(tasksResponse?.pagination?.count ?? 0);

  const handleSearchOnChange = debounce((e) => {
    setFilters((prevState) => {
      return {
        ...prevState,
        idCodeName: {
          label:
            e.target.value.length > 0
              ? t('profile.idCodeName') + ' - ' + e.target.value
              : '',
          value: e.target.value
        }
      };
    });
  }, 500);

  const handleTaskExecutionProgressStatusChange = useCallback(
    async (task: ITask, nextStatuses: ExecutionProgressStatusFormValue[]) => {
      try {
        await syncExecutionProgressStatuses(
          task.id,
          task.execution_progress_statuses,
          nextStatuses
        );
        await queryClient.invalidateQueries({
          queryKey: tasksKeys.list(companyId, filtersForQuery)
        });
        toast.success(t('common.success'));
      } catch {
        toast.error(t('anErrorOccurred'));
      }
    },
    [companyId, filtersForQuery, queryClient, t]
  );

  const handleSubmitTaskForm = async (data: ITaskForm['task']) => {
    try {
      if (loggedUserData?.id) {
        const {
          taskType,
          priority,
          task_participations_attributes,
          execution_progress_statuses_attributes,
          start_time,
          completion_time,
          ...restData
        } = data;

        const actualTaskParticipationsAttributes = selectedItemData
          ? [
              // participants included in fetched list and not in selected list are removed
              ...selectedItemData.task_participations
                .filter(
                  (taskParticipation) =>
                    !task_participations_attributes?.find(
                      (taskParticipationAttribute) =>
                        taskParticipationAttribute.value.toString() ===
                        taskParticipation.participant?.id.toString()
                    )
                )
                .map((taskParticipation) => ({
                  id: taskParticipation.id,
                  _destroy: 1
                })),
              // participants included in selected list and not in fetched list are removed
              ...(
                task_participations_attributes?.filter(
                  (taskParticipationAttribute) =>
                    !selectedItemData.task_participations.find(
                      (taskParticipation) =>
                        taskParticipationAttribute.value.toString() ===
                        taskParticipation.participant.id.toString()
                    )
                ) || []
              ).map((taskParticipationAttribute) => ({
                participant_id: taskParticipationAttribute.value,
                function_id: taskParticipationAttribute.function_id
              })),
              // participants included in both but with different function_id
              ...(
                selectedItemData.task_participations?.filter(
                  (taskParticipation) =>
                    task_participations_attributes?.find(
                      (taskParticipationAttribute) =>
                        taskParticipationAttribute.value.toString() ===
                          taskParticipation.participant?.id.toString() &&
                        taskParticipationAttribute.function_id?.toString() !==
                          taskParticipation.function?.id.toString()
                    )
                ) || []
              ).map((taskParticipation) => ({
                id: taskParticipation.id,
                function_id: task_participations_attributes?.find(
                  (taskParticipationAttribute) =>
                    taskParticipationAttribute.value.toString() ===
                    taskParticipation.participant?.id.toString()
                )?.function_id
              }))
            ]
          : task_participations_attributes?.map(
              (taskParticipationAttribute) => ({
                participant_id: taskParticipationAttribute.value,
                function_id: taskParticipationAttribute.function_id
              })
            );

        const payload = {
          task: {
            ...restData,
            task_type_id: taskType?.value,
            priority_level_id: priority?.value,
            author_id: loggedUserData.id,
            start_time: start_time
              ? start_time
              : execution_progress_statuses_attributes?.find(
                  (statusAttribute) => statusAttribute.kind === 'P'
                )?.confirmed_at,
            completion_time: completion_time
              ? completion_time
              : execution_progress_statuses_attributes?.find(
                  (statusAttribute) => {
                    const executionProgressTracking = executionProgressTrackings.find(
                      ({ id }) =>
                        id === statusAttribute.execution_progress_tracking_id
                    );

                    return (
                      executionProgressTracking?.kind === 'PDCA' &&
                      statusAttribute.kind === 'A'
                    );
                  }
                )?.confirmed_at,
            task_participations_attributes: actualTaskParticipationsAttributes
          }
        };

        if (selectedItemData) {
          await editTask(selectedItemData.id, payload);
          await syncExecutionProgressStatuses(
            selectedItemData.id,
            selectedItemData.execution_progress_statuses,
            execution_progress_statuses_attributes
          );
          await queryClient.invalidateQueries({
            queryKey: tasksKeys.list(companyId, filtersForQuery)
          });
          setIsFormModalOpen(false);
          toast.success(t('common.success'));
          setSelectedItemData(undefined);
        } else {
          const { data: createdTask } = await createTask(payload);

          if (createdTask?.id) {
            await syncExecutionProgressStatuses(
              createdTask.id,
              [],
              execution_progress_statuses_attributes
            );
          }

          await queryClient.invalidateQueries({
            queryKey: tasksKeys.list(companyId, filtersForQuery)
          });
          setIsFormModalOpen(false);
          toast.success(t('common.success'));
          setSelectedItemData(undefined);
        }
      }
    } catch (e) {
      console.error(e);

      toast.error(t('anErrorOccurred'));
    }
  };

  const handleDeleteTask = async () => {
    try {
      if (selectedItemData) {
        await deleteTask(selectedItemData.id);
        await queryClient.invalidateQueries({
          queryKey: tasksKeys.list(companyId, filtersForQuery)
        });
        setIsFormModalOpen(false);
        toast.success(t('common.success'));
        setSelectedItemData(undefined);
      }
    } catch (e) {
      toast.error(t('anErrorOccurred'));
    }
  };

  const handleLoadExecutionProgressTrackings = async () => {
    const { data } = await getExecutionProgressTrackings();

    setExecutionProgressTrackings(data);
  };

  useEffect(() => {
    handleLoadExecutionProgressTrackings();
  }, []);

  const header: (string | JSX.Element | DefaultTFuncReturn)[] = [
    ...(areItemsSelectable
      ? [
          <Checkbox
            key="headerCheckbox"
            checked={tasks.every(({ id }) => checkedItemsIds?.includes(id))}
            onChange={() => {
              if (tasks.every(({ id }) => checkedItemsIds?.includes(id))) {
                setCheckedItemsIds([]);
              } else {
                setCheckedItemsIds(tasks.map(({ id }) => id));
              }
            }}
          />
        ]
      : []),
    <FontAwesomeIcon key="icon-faStar" icon={faStar} />,
    t('tasks.task.table.practice'),
    t('tasks.task.table.name'),
    t('tasks.task.table.taskType'),
    t('tasks.task.table.responsible'),
    t('tasks.task.table.area'),
    <FontAwesomeIcon
      key="icon-faTriangleExclamation"
      icon={faTriangleExclamation}
    />,
    t('tasks.task.table.status'),
    t('tasks.task.table.deadlineRealisation'),
    t('tasks.task.table.timeRealisation'),
    t('tasks.task.table.daysCount'),
    ''
  ];

  const rowButtons = ({ item }: { item: ITask }) => (
    <Flex style={{ gap: '10px' }} mx="auto" width="fit-content">
      <Button
        variant="green"
        onClick={() => {
          console.log(111);
        }}
        icon={<FontAwesomeIcon icon={faCirclePlay} />}
      />
      <Button
        variant="grey"
        bordered
        onClick={() => {
          console.log(222);
        }}
        icon={<FontAwesomeIcon icon={faClipboard} />}
      />
      <Button
        onClick={() => {
          setIsFormModalOpen((prevIsFormModalOpen) => !IsFormModalOpen);
          setSelectedItemData(item);
        }}
        icon={<FontAwesomeIcon icon={faEdit} />}
      />
    </Flex>
  );

  const parseData = (data: ITask[]) => {
    return data.map((el) => {
      return {
        ...(areItemsSelectable && {
          checkbox: (
            <Checkbox
              checked={checkedItemsIds?.includes(el.id)}
              onChange={() => {
                if (checkedItemsIds?.includes(el.id)) {
                  setCheckedItemsIds((prevCheckedItemsIds) =>
                    prevCheckedItemsIds.filter(
                      (prevCheckedItemsId) => prevCheckedItemsId !== el.id
                    )
                  );
                } else {
                  setCheckedItemsIds((prevCheckedItemsIds) => [
                    ...prevCheckedItemsIds,
                    el.id
                  ]);
                }
              }}
            />
          )
        }),
        star: (
          <>
            <Button
              variant="secondary"
              transparent
              id={'starTooltip' + el.id}
              data-tooltip-content={t('tasks.task.table.star') || ''}
              icon={<TaskStar task={el} />}
              onClick={() => {
                setSelectedItemData(el);
                setIsStarModalOpen(
                  (prevIsStarModalOpen) => !prevIsStarModalOpen
                );
              }}
            />
            <Tooltip anchorId={'starTooltip' + el.id} />
          </>
        ),
        practice: (
          <>
            <Flex justifyContent="center">
              <Image
                id={'practiceTooltip' + el.id}
                data-tooltip-html={ReactDOMServer.renderToStaticMarkup(
                  <Flex flexDirection="column" alignItems="center">
                    <StyledP variant="body">{el.id}</StyledP>
                    <StyledP variant="body">{el.name}</StyledP>
                    <StyledP variant="body">
                      <FontAwesomeIcon icon={faUserPen} /> {el.author.email}
                    </StyledP>
                  </Flex>
                )}
              />
              <StyledP variant="body">{el.practice?.name}</StyledP>
            </Flex>
            <Tooltip anchorId={'practiceTooltip' + el.id} />
          </>
        ),
        name: (
          <Box
            minWidth={nameColMinWidth ? nameColMinWidth : '300px'}
            onClick={() => {
              setIsFormModalOpen((prevIsFormModalOpen) => !IsFormModalOpen);
              setSelectedItemData(el);
            }}
          >
            <StyledP variant="body">{el.name}</StyledP>
          </Box>
        ),
        taskType: <StyledP variant="body">{el.task_type?.name}</StyledP>,
        responsible: (
          <>
            <Flex
              justifyContent="flex-start"
              alignItems="center"
              id={'responsibleTooltip' + el.id}
              data-tooltip-content={
                t('tasks.task.table.tooltips.responsible') || ''
              }
            >
              <Avatar size="extra-small" />
              <Box ml={2}>
                <StyledP variant="body">{el.author.username}</StyledP>
              </Box>
            </Flex>
            <Tooltip anchorId={'responsibleTooltip' + el.id} />
          </>
        ),
        area: (
          <StyledP variant="body">
            {el.areas.map(({ name }) => name).join(', ')}
          </StyledP>
        ),
        priority: el.priority_level ? (
          <>
            <span
              id={'priorityTooltip' + el.id}
              data-tooltip-content={displayTranslatedField(el.priority_level, 'name')}
            >
              <PriorityLevelBadge
                priorityLevel={el.priority_level}
                showName={false}
              />
            </span>
            <Tooltip anchorId={'priorityTooltip' + el.id} />
          </>
        ) : null,
        status: (
          <Flex justifyContent="center">
            <Box>
              <ExecutionProgressTracking
                id={`${el.id}-ExecutionProgressTracking`}
                executionProgressTracking={executionProgressTrackings.find(
                  ({ id }) =>
                    id === el.execution_progress_statuses?.[0]?.execution_progress_tracking_id
                )}
                executionProgressStatuses={el.execution_progress_statuses}
                usersList={usersList}
                onChange={(nextStatuses) =>
                  handleTaskExecutionProgressStatusChange(el, nextStatuses)
                }
              />
            </Box>
          </Flex>
        ),
        deadlineRealisation: (
          <div
            style={{
              // TODO: this needs to fill the entire background of this table cell.
              // since it's controlled by some react Table, probably simplest would be adding a class and doing `td > .something` in CSS
              // but maybe there's a way to do it properly?
              background:
                el.completion_time &&
                el.planned_completion_date &&
                !!isAfter(
                  new Date(el.completion_time),
                  new Date(el.planned_completion_date)
                )
                  ? theme.palette.accent.lightRed
                  : '',
              padding: '8px'
            }}
          >
            <StyledP variant="body" textAlign='center'>
              {format(new Date(el.completion_time), dateFormat)}
            </StyledP>
          </div>
        ),
        timeRealisation: (
          <StyledP variant="body" textAlign='center'>
            {el.duration_iso8601 &&
              `${durationIso8601ToHour(
                el.duration_iso8601
              )}${t('tasks.task.table.taskTimeHours')} ${durationIso8601ToMinute(el.duration_iso8601)}${t('tasks.task.table.taskTimeMinutes')}`}
          </StyledP>
        ),
        daysCount: (() => {
          const daysToPlannedCompletionDate = differenceInDays(
            new Date(el.planned_completion_date),
            new Date()
          );

          return (
            <StyledP variant='body' textAlign='center' style={{
              color: daysToPlannedCompletionDate < 0? theme.palette.accent.red : theme.palette.accent.green
            }}>
              {daysToPlannedCompletionDate}
            </StyledP>
          );
        })(),
        options: rowButtons({ item: el })
      };
    });
  };

  const switchableItems = [
    'star',
    'practice',
    'name',
    'taskType',
    'responsible',
    'area',
    'status',
    'deadlineRealisation',
    'timeRealisation',
    'daysCount'
  ];

  const additionalHeader = (
    <Flex width="100%" flexDirection="column">
      <Flex
        width="100%"
        gap="10px"
        justifyContent="space-between"
        alignItems="center"
      >
        {showDetailedHeader && (
          <DetailedHeaderWrapper
            ml={-18}
            my={-2}
            py={14}
            px={2}
            minWidth="620px"
          >
            <TopBar
              customTitle={customTitle}
              setIsFormModalOpen={setIsFormModalOpen}
              paginationTotal={paginationTotal}
            />
          </DetailedHeaderWrapper>
        )}

        <FontAwesomeIcon icon={faSearch} />
        <TextField
          placeholder={t('employeesAndUsersView.form.search') ?? undefined}
          hideError
          onChange={(e) => {
            handleSearchOnChange(e);
          }}
        />
        <ColumnsOptions
          hiddenItems={hiddenItems}
          setHiddenItems={setHiddenItems}
          switchableItems={switchableItems}
          labelPrefix="tasks.task.table."
        />
      </Flex>
      {checkedItemsIds?.length ? (
        <ChosenItemsHeader>
          <Flex alignItems="center">
            <P variant="body" color="secondary">
              {checkedItemsIds?.length} - {t('tasks.chosenLines')}
            </P>
            <Button transparent variant="secondary">
              {t('common.uncheck')}
            </Button>
          </Flex>
          <Flex gap="4px">
            {checkedItemsIds?.length === 1 && (
              <Button
                variant="red"
                onClick={() => {
                  setIsCancelTaskModalOpen(
                    (prevIsCancelTaskModalOpen) => !prevIsCancelTaskModalOpen
                  );
                }}
              >
                {t('common.cancel')}
              </Button>
            )}
            <Button
              onClick={() => {
                setIsArchiveTaskModalOpen(
                  (prevIsArchiveTaskModalOpen) => !prevIsArchiveTaskModalOpen
                );
              }}
            >
              {t('tasks.toArchive')}
            </Button>
            <Button>{t('tasks.exportToCalendar')}</Button>
            <Button
              onClick={() => {
                setIsRemindLatecomersModalOpen(
                  (prevIsRemindLatecomersModalOpen) =>
                    !prevIsRemindLatecomersModalOpen
                );
              }}
            >
              {t('remindLatecomers')}
            </Button>
            <Button
              onClick={() => {
                setIsSendNotificationsModalOpen(
                  (prevIsSendNotificationsModalOpen) =>
                    !prevIsSendNotificationsModalOpen
                );
              }}
            >
              {t('tasks.sendNotificationsToSelectedOnes')}
            </Button>
          </Flex>
        </ChosenItemsHeader>
      ) : (
        <></>
      )}
    </Flex>
  );

  return (
    <>
      <Wrapper>
        {showTopBar && (
          <TopBar
            customTitle={customTitle}
            setIsFormModalOpen={setIsFormModalOpen}
            paginationTotal={paginationTotal}
          />
        )}

        <Box my={2}>
          <Table
            header={header}
            data={parseData(tasks)}
            pagination={{
              page: page,
              setPage: setPage,
              perPage: perPage,
              total: paginationTotal,
              setPerPage: setPerPage
            }}
            additionalHeaderElement={showAdditionalHeader && additionalHeader}
            hiddenItems={hiddenItems}
            fontSize="big"
            containerHeight={tableContainerHeight}
          />
        </Box>
      </Wrapper>
      <TaskFormModal
        data={selectedItemData}
        setSelectedItemData={setSelectedItemData}
        handleSubmitTaskForm={handleSubmitTaskForm}
        isOpen={IsFormModalOpen}
        onCancelClick={() => {
          setIsFormModalOpen(false);
          setSelectedItemData(undefined);
        }}
        handleDeleteTask={handleDeleteTask}
        handleLoadTasks={refetchTasks}
      />
      <CancelTaskModal
        isOpen={IsCancelTaskModalOpen}
        onCancelClick={() => {
          setIsCancelTaskModalOpen(false);
        }}
      />
      <Modal
        gridTemplateColumns="45%"
        isOpen={IsArchiveTaskModalOpen}
        onCancelClick={() => {
          setIsArchiveTaskModalOpen(false);
        }}
        headerTitle={t('tasks.archiveTaskConfirmation?')}
        mainButton={{
          action: () => {},
          variant: 'eucalyptus',
          label: t('common.yes')
        }}
        isSecondButtonVisible
      />
      <RemindLatecomersModal
        isOpen={IsRemindLatecomersModalOpen}
        onCancelClick={() => {
          setIsRemindLatecomersModalOpen(false);
        }}
      />
      <SendNotificationsModal
        isOpen={IsSendNotificationsModalOpen}
        onCancelClick={() => {
          setIsSendNotificationsModalOpen(false);
        }}
      />
      <StarModal
        selectedItemData={selectedItemData}
        isOpen={isStarModalOpen}
        onSuccess={async () => {
          await refetchTasks();
          setIsStarModalOpen(false);
        }}
        onCancelClick={() => {
          setIsStarModalOpen(false);
        }}
      />
    </>
  );
};
